import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import CTTeacherExecutiveTab from "@/components/CTTeacherExecutiveTab";
import CTTeacherProfileTab from "@/components/CTTeacherProfileTab";
import CTTeacherDeploymentTab from "@/components/CTTeacherDeploymentTab";
import CTTeacherTrainingTab from "@/components/CTTeacherTrainingTab";
import CTTeacherInsightsTab from "@/components/CTTeacherInsightsTab";
import "@/components/CTTeacherExecutiveTab.css";
import "@/components/CTTeacherProfileTab.css";
import "@/components/CTTeacherDeploymentTab.css";
import "@/components/CTTeacherTrainingTab.css";
import "@/components/CTTeacherInsightsTab.css";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Users, 
  RefreshCw,
  Upload,
  School,
  BarChart3,
  Target,
  GraduationCap,
  Award,
  Clock,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  UserCheck,
  Brain
} from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import DashboardPageHeader from "@/components/DashboardPageHeader";
import { BlockLink } from "@/components/DrilldownLink";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend
} from "recharts";

import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const TAB_ITEMS = [
  { id: "executive", label: "Executive Overview", icon: BarChart3 },
  { id: "profile", label: "Teacher Profile", icon: Users },
  { id: "deployment", label: "Deployment & Risk", icon: Target },
  { id: "training", label: "Training & Certification", icon: GraduationCap },
  { id: "insights", label: "Insights", icon: Brain },
];

const HEADER_COPY = {
  executive: {
    eyebrow: "Teacher Data · Pune District · 2025–26",
    subtitle:
      "Executive overview redesigned for faster decision-making: workforce scale, compliance readiness, gender mix, block load and action priorities are visible without the current clutter.",
    icon: BarChart3,
  },
  profile: {
    eyebrow: "Teacher profile · Pune District · 2025–26",
    subtitle:
      "Teacher workforce profile redesigned for faster scanning of gender balance, age structure, social category mix, qualifications and risk signals.",
    icon: Users,
  },
  deployment: {
    eyebrow: "Deployment & Risk · Pune District · 2025–26",
    subtitle:
      "Teacher deployment, service tenure, retirement exposure and compliance quality redesigned as an executive risk cockpit.",
    icon: Target,
  },
  training: {
    eyebrow: "Teacher Data · 2025–26 · Pune District",
    subtitle:
      "Training and certification view redesigned for faster executive scanning, clearer gap ownership, and action-oriented capacity building.",
    icon: GraduationCap,
  },
  insights: {
    eyebrow: "Teacher Insights · Pune District · 2025–26",
    subtitle:
      "AI-assisted teacher workforce insights with root-cause signals, recommended interventions and priority actions for district review.",
    icon: Brain,
  },
};

// KPI Card Component
const KPICard = ({ label, value, suffix = "", icon: Icon, color = "blue", description }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    cyan: "bg-cyan-50 text-cyan-600",
    pink: "bg-pink-50 text-pink-600",
  };

  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">
                {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
              </span>
              {suffix && <span className="text-lg text-slate-500">{suffix}</span>}
            </div>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colors[color]}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const CTTeacherDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [blockData, setBlockData] = useState([]);
  const [genderData, setGenderData] = useState([]);
  const [socialCategory, setSocialCategory] = useState([]);
  const [qualification, setQualification] = useState(null);
  const [ageData, setAgeData] = useState(null);
  const [tenureData, setTenureData] = useState(null);
  const [trainingData, setTrainingData] = useState([]);
  const [dataQuality, setDataQuality] = useState(null);
  const [certification, setCertification] = useState(null);
  const [activeTab, setActiveTab] = useState("executive");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // The axios interceptor in App.js automatically adds scope parameters from localStorage
      // We can optionally pass them explicitly here, but the interceptor will merge them
      // Build scope parameters - the axios interceptor will merge them with localStorage values
      // Also pass district_name, block_name, school_name to help with matching
      const requestParams = {};
      if (scope.districtCode && scope.districtCode.trim()) {
        requestParams.district_code = scope.districtCode.trim();
      }
      if (scope.districtName && scope.districtName.trim()) {
        requestParams.district_name = scope.districtName.trim();
      }
      if (scope.blockCode && scope.blockCode.trim()) {
        requestParams.block_code = scope.blockCode.trim();
      }
      if (scope.blockName && scope.blockName.trim()) {
        requestParams.block_name = scope.blockName.trim();
      }
      if (scope.udiseCode && scope.udiseCode.trim()) {
        requestParams.udise_code = scope.udiseCode.trim();
      }
      if (scope.schoolName && scope.schoolName.trim()) {
        requestParams.school_name = scope.schoolName.trim();
      }

      console.log('Fetching CTTeacher data with scope:', {
        districtCode: scope.districtCode,
        blockCode: scope.blockCode,
        udiseCode: scope.udiseCode,
        params: requestParams,
        apiUrl: API
      });

      // Make API calls - the interceptor will merge scope params from localStorage
      const [
        overviewRes,
        blockRes,
        genderRes,
        socialRes,
        qualRes,
        ageRes,
        tenureRes,
        trainingRes,
        qualityRes,
        certRes
      ] = await Promise.all([
        axios.get(`${API}/ctteacher/overview`, { params: requestParams }),
        axios.get(`${API}/ctteacher/block-wise`, { params: requestParams }),
        axios.get(`${API}/ctteacher/gender-distribution`, { params: requestParams }),
        axios.get(`${API}/ctteacher/social-category`, { params: requestParams }),
        axios.get(`${API}/ctteacher/qualification`, { params: requestParams }),
        axios.get(`${API}/ctteacher/age-distribution`, { params: requestParams }),
        axios.get(`${API}/ctteacher/service-tenure`, { params: requestParams }),
        axios.get(`${API}/ctteacher/training-demand`, { params: requestParams }),
        axios.get(`${API}/ctteacher/data-quality`, { params: requestParams }),
        axios.get(`${API}/ctteacher/certification`, { params: requestParams })
      ]);
      
      // Log successful fetch
      const overviewData = overviewRes?.data || {};
      console.log('CTTeacher data fetched successfully:', {
        overviewTotalTeachers: overviewData.total_teachers,
        overviewTotalSchools: overviewData.total_schools,
        blockDataCount: Array.isArray(blockRes?.data) ? blockRes.data.length : 0,
        hasGenderData: Array.isArray(genderRes?.data) && genderRes.data.length > 0,
        hasQualification: !!qualRes?.data,
        hasAgeData: !!ageRes?.data
      });
      
      // Set overview data - use the API response directly, with fallbacks for missing fields
      if (overviewData && typeof overviewData === "object" && (overviewData.total_teachers ?? 0) > 0) {
        setOverview(overviewData);
      } else {
        setOverview(null);
      }
      
      // Set other data arrays/objects
      setBlockData(Array.isArray(blockRes?.data) ? blockRes.data : []);
      setGenderData(Array.isArray(genderRes?.data) ? genderRes.data : []);
      setSocialCategory(Array.isArray(socialRes?.data) ? socialRes.data : []);
      setQualification(qualRes?.data || null);
      setAgeData(ageRes?.data || null);
      setTenureData(tenureRes?.data || null);
      setTrainingData(Array.isArray(trainingRes?.data) ? trainingRes.data : []);
      setDataQuality(qualityRes?.data || null);
      setCertification(certRes?.data || null);
    } catch (error) {
      console.error("Error fetching CTTeacher data:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        code: error.code
      });
      
      // Handle network errors (including ERR_BLOCKED_BY_CLIENT from browser extensions)
      if (error.code === 'ERR_NETWORK' || error.message?.includes('ERR_BLOCKED_BY_CLIENT') || error.message?.includes('Failed to fetch')) {
        console.warn("Network error detected - may be caused by browser extension");
        toast.error("Network error: Please disable ad blockers or privacy extensions and try again");
        // Don't reset data on network errors - keep existing data visible
        setLoading(false);
        return;
      }
      
      const errorMsg = error.response?.data?.detail || error.message || "Failed to load teacher data";
      
      // Only show error toast for actual errors, not for empty results
      if (error.response?.status && error.response?.status !== 404 && error.response?.status >= 500) {
        toast.error(errorMsg);
      }
      
      // Reset data on error to show "No Data" message (but not on network errors)
      if (error.response) {
        setOverview(null);
        setBlockData([]);
        setGenderData([]);
        setSocialCategory([]);
        setQualification(null);
        setAgeData(null);
        setTenureData(null);
        setTrainingData([]);
        setDataQuality(null);
        setCertification(null);
      }
    } finally {
      setLoading(false);
    }
  }, [
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
    scope.schoolName,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [uploading, setUploading] = useState(false);

  const handleImport = async () => {
    const url = "https://customer-assets.emergentagent.com/job_e600aca7-d1b5-4003-a850-c6b4b2f65c48/artifacts/7h74ajig_8.%20CTTeacher%20Data%202025-26.xlsx";
    
    setImporting(true);
    try {
      await axios.post(`${API}/ctteacher/import?url=${encodeURIComponent(url)}`);
      toast.success("CTTeacher data import started!");
      setTimeout(() => {
        fetchData();
        setImporting(false);
      }, 30000); // Large file needs more time to process
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  const handleUploadFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Please select an Excel file (.xlsx or .xls)");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/ctteacher/import/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Upload started. Data will load in the background. Refresh in 1–2 min and check Executive Dashboard → Teacher & Staffing → Risk Metrics.");
      setTimeout(() => { fetchData(); setUploading(false); }, 60000);
    } catch (error) {
      toast.error("Upload failed: " + (error.response?.data?.detail || error.message));
      setUploading(false);
    }
    e.target.value = "";
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  // Check if we have data - overview should exist and have meaningful data
  const hasData = overview && typeof overview === 'object' && (
    (overview.total_teachers > 0) || 
    (overview.total_schools > 0) ||
    (Object.keys(overview).length > 3)
  );
  
  // Debug log to help diagnose data loading issues
  if (overview) {
    console.log('Dashboard data check:', {
      hasData,
      totalTeachers: overview.total_teachers,
      totalSchools: overview.total_schools,
      overviewKeys: Object.keys(overview).length,
      overviewSample: Object.keys(overview).slice(0, 5)
    });
  }

  const copy = HEADER_COPY[activeTab] || HEADER_COPY.executive;
  const EyebrowIcon = copy.icon;

  return (
    <div className="ctteacher-dashboard space-y-6 animate-fade-in" data-testid="ctteacher-dashboard">
      <DashboardPageHeader
        title="CTTeacher Analytics Dashboard"
        subtitle={
          hasData
            ? copy.subtitle
            : "Teacher Data • 2025-26 • Pune District"
        }
        eyebrow={
          hasData ? (
            <>
              <EyebrowIcon size={14} />
              {copy.eyebrow}
            </>
          ) : null
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImport}
              disabled={importing || uploading}
              data-testid="import-ctteacher-btn"
            >
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Import from URL"}
            </Button>
            <label className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background hover:bg-accent h-9 px-4 cursor-pointer disabled:opacity-50">
              <input type="file" accept=".xlsx,.xls" className="sr-only" onChange={handleUploadFile} disabled={importing || uploading} />
              {uploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? "Uploading..." : "Upload Excel (real data)"}
            </label>
            <ExportPanel dashboardName="ctteacher" dashboardTitle="CT Teacher Analytics" />
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </>
        }
      />

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Teacher Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the CTTeacher Excel file</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-ctteacher-empty-btn">
              {importing ? "Importing..." : "Import Teacher Data"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Dashboard Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="ctex-tabs-wrap" aria-label="CTTeacher dashboard sections">
              <nav className="ctex-tabs">
                {TAB_ITEMS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`ctex-tab ${activeTab === id ? "active" : ""}`}
                    onClick={() => setActiveTab(id)}
                    data-testid={`tab-${id}`}
                  >
                    <Icon />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <TabsContent value="executive" className="mt-0">
              <CTTeacherExecutiveTab
                overview={overview}
                blockData={blockData}
                genderData={genderData}
                onNavigateTab={setActiveTab}
              />
            </TabsContent>

            <TabsContent value="profile" className="mt-0">
              <CTTeacherProfileTab
                overview={overview}
                genderData={genderData}
                ageData={ageData}
                socialCategory={socialCategory}
                qualification={qualification}
                blockData={blockData}
              />
            </TabsContent>

            <TabsContent value="deployment" className="mt-0">
              <CTTeacherDeploymentTab
                tenureData={tenureData}
                ageData={ageData}
                dataQuality={dataQuality}
              />
            </TabsContent>

            <TabsContent value="training" className="mt-0">
              <CTTeacherTrainingTab
                certification={certification}
                trainingData={trainingData}
              />
            </TabsContent>
            <TabsContent value="insights" className="mt-0">
              <CTTeacherInsightsTab overview={overview} blockData={blockData} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default CTTeacherDashboard;
